import { useState, useEffect, useCallback } from 'react';
import { Sparkles, Save, Loader2, Check, BookOpen } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';
import { API } from './constants';

const SECTIONS = [
  {
    key: 'pitch',
    label: 'Pitch / Presentation',
    placeholder: "Ex: Le Mas est un restaurant-traiteur familial a Saint-Laurent-de-la-Salanque, specialise en cuisine catalane fait-maison. Laurent Sanmartin, le patron, met un point d'honneur a utiliser des produits locaux du Roussillon...",
    help: "Qui vous etes, votre histoire, ce qui vous differencie. L'IA l'utilisera pour se presenter.",
  },
  {
    key: 'specialites',
    label: 'Specialites & Points forts',
    placeholder: "Ex: Paella geante au feu de bois (jusqu'a 300 pers.), BBQ plancha en exterieur, plateaux de fruits de mer, cuisine catalane traditionnelle, menu vegan disponible, bar a tapas...",
    help: "Vos plats phares, types de cuisine, capacites speciales. L'IA les mettra en avant.",
  },
  {
    key: 'references',
    label: 'References clients & Evenements',
    placeholder: "Ex: Partenaire officiel USAP et Dragons Catalans. Traiteur du gala des Toques Blanches 2025. Plus de 200 mariages realises. Repas pour le CE de la SNCF Perpignan (320 pers.)...",
    help: "Vos realisations passees. L'IA s'en servira comme preuves sociales dans les emails.",
  },
  {
    key: 'ton',
    label: 'Ton & Style de communication',
    placeholder: "Ex: Je veux un ton chaleureux mais professionnel. Pas trop commercial, plutot comme si je parlais a un ami. Toujours vouvoyer. Signer 'Laurent' pas 'M. Sanmartin'. Ne jamais utiliser 'n'hesitez pas'...",
    help: "Comment vous voulez que l'IA communique en votre nom. Soyez precis !",
  },
  {
    key: 'infos_pratiques',
    label: 'Infos pratiques',
    placeholder: "Ex: Capacite 150 pers. en salle, 300 en exterieur. Parking 50 places. Acces PMR. On se deplace dans tout le 66, 11 et 34. Livraison possible. Materiel complet (tables, nappes, vaisselle)...",
    help: "Capacites, zones de livraison, equipements. L'IA les citera quand c'est pertinent.",
  },
  {
    key: 'prompts_perso',
    label: 'Prompts & Instructions perso',
    placeholder: "Collez ici vos textes prepares avec Claude, vos instructions specifiques pour l'IA, des exemples d'emails qui ont bien marche, etc.",
    help: "Champ libre — tout ce que vous voulez que l'IA sache et utilise. C'est la section prioritaire.",
  },
];

export function CrmIAKnowledge() {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [expandedSection, setExpandedSection] = useState('pitch');

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/crm/ia-knowledge`);
      const d = await res.json();
      setData(d);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/crm/ia-knowledge`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (res.ok) {
        toast.success('Base de connaissances sauvegardee !');
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      }
    } catch {
      toast.error('Erreur sauvegarde');
    }
    setSaving(false);
  };

  const filledCount = SECTIONS.filter(s => data[s.key] && data[s.key].trim().length > 10).length;
  const totalChars = SECTIONS.reduce((sum, s) => sum + (data[s.key] || '').length, 0);

  if (loading) return <div className="flex justify-center py-12"><Loader2 size={20} className="animate-spin text-purple-500" /></div>;

  return (
    <div className="space-y-4" data-testid="crm-ia-knowledge">
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles size={16} className="text-purple-500" />
          <h3 className="text-sm font-bold text-purple-700">Base de connaissances IA</h3>
        </div>
        <p className="text-xs text-purple-600 leading-relaxed">
          Renseignez ces informations pour que l'IA genere des emails, relances et scripts
          adaptes a VOTRE restaurant. Plus c'est precis, meilleurs seront les resultats.
        </p>
        <div className="flex items-center gap-3 mt-3">
          <div className="flex items-center gap-1.5">
            <div className="flex gap-0.5">
              {SECTIONS.map((s, i) => (
                <div key={i} className={`w-5 h-1.5 rounded-full ${data[s.key] && data[s.key].trim().length > 10 ? 'bg-purple-500' : 'bg-purple-200'}`} />
              ))}
            </div>
            <span className="text-[10px] text-purple-500 font-medium">{filledCount}/{SECTIONS.length} sections</span>
          </div>
          {totalChars > 0 && (
            <span className="text-[10px] text-purple-400">{totalChars} caracteres</span>
          )}
        </div>
      </div>

      <div className="space-y-2">
        {SECTIONS.map(section => {
          const isExpanded = expandedSection === section.key;
          const hasContent = data[section.key] && data[section.key].trim().length > 10;
          return (
            <div key={section.key} className={`bg-white rounded-xl border transition-all ${isExpanded ? 'border-purple-300 shadow-sm' : hasContent ? 'border-green-200' : 'border-gray-200'}`}>
              <button
                onClick={() => setExpandedSection(isExpanded ? '' : section.key)}
                className="w-full flex items-center justify-between px-4 py-3 text-left"
                data-testid={`kb-section-${section.key}`}
              >
                <div className="flex items-center gap-2">
                  {hasContent ? (
                    <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center"><Check size={10} className="text-white" /></div>
                  ) : (
                    <div className="w-5 h-5 rounded-full border-2 border-gray-200" />
                  )}
                  <span className={`text-xs font-medium ${isExpanded ? 'text-purple-700' : 'text-gray-700'}`}>{section.label}</span>
                </div>
                <span className="text-[10px] text-gray-300">{isExpanded ? '\u25B2' : '\u25BC'}</span>
              </button>
              {isExpanded && (
                <div className="px-4 pb-4 space-y-2">
                  <p className="text-[10px] text-gray-400">{section.help}</p>
                  <textarea
                    value={data[section.key] || ''}
                    onChange={e => setData(prev => ({ ...prev, [section.key]: e.target.value }))}
                    placeholder={section.placeholder}
                    rows={6}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none focus:border-purple-400 focus:ring-1 focus:ring-purple-200 outline-none leading-relaxed"
                    data-testid={`kb-input-${section.key}`}
                  />
                  {data[section.key] && (
                    <p className="text-[9px] text-gray-300 text-right">{(data[section.key] || '').length} caracteres</p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Button onClick={save} disabled={saving} className="w-full bg-purple-600 hover:bg-purple-700 text-white" data-testid="kb-save-btn">
        {saving ? <><Loader2 size={14} className="animate-spin mr-2" /> Sauvegarde...</>
          : saved ? <><Check size={14} className="mr-2" /> Sauvegarde !</>
          : <><Save size={14} className="mr-2" /> Sauvegarder la base de connaissances</>}
      </Button>

      <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
        <div className="flex items-center gap-2 mb-1.5">
          <BookOpen size={12} className="text-gray-400" />
          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Comment ca marche ?</span>
        </div>
        <div className="space-y-1 text-[10px] text-gray-500 leading-relaxed">
          <p>1. Remplissez les sections avec vos infos (preparez avec Claude si vous voulez)</p>
          <p>2. Sauvegardez</p>
          <p>3. L'IA utilisera automatiquement ces infos pour TOUS les emails, relances, scripts et suggestions</p>
          <p>4. Plus vous etes precis, plus l'IA sera pertinente et personnalisee</p>
        </div>
      </div>
    </div>
  );
}
